import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog';
import { sellersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatRelativeTime, getErrorMessage } from '../lib/utils';
import { ListingGrid } from '../components/listings/ListingGrid';
import { toast } from 'sonner';
import {
  MapPin,
  Phone,
  Shield,
  Building,
  User,
  Globe,
  Star,
  Calendar,
  Package,
  MessageSquare,
} from 'lucide-react';

const StarRow = ({ value = 0, size = 'h-5 w-5', onClick }) => (
  <div className="flex items-center gap-0.5" data-testid="star-row">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={onClick ? () => onClick(n) : undefined}
        className={onClick ? 'cursor-pointer' : 'cursor-default'}
        disabled={!onClick}
        data-testid={onClick ? `star-input-${n}` : undefined}
      >
        <Star
          className={`${size} ${
            n <= Math.round(value) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/40'
          }`}
        />
      </button>
    ))}
  </div>
);

export default function SellerProfilePage() {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();

  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, listingsRes, reviewsRes] = await Promise.all([
        sellersAPI.getProfile(id),
        sellersAPI.getListings(id, { limit: 24 }),
        sellersAPI.getReviews(id, { limit: 20 }),
      ]);
      setProfile(profileRes.data);
      setListings(listingsRes.data);
      setReviews(reviewsRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo cargar el perfil del vendedor'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSubmitReview = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para dejar una reseña');
      return;
    }
    setSubmittingReview(true);
    try {
      await sellersAPI.createReview(id, {
        rating: reviewRating,
        comment: reviewComment.trim() || null,
      });
      toast.success('Reseña enviada');
      setReviewDialogOpen(false);
      setReviewComment('');
      setReviewRating(5);
      await loadAll();
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo enviar la reseña'));
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 space-y-6">
        <Skeleton className="h-40 w-full rounded-md" />
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 text-center">
        <h1 className="font-heading text-3xl font-bold uppercase mb-4">Vendedor no encontrado</h1>
        <Link to="/browse">
          <Button className="btn-industrial">Explorar anuncios</Button>
        </Link>
      </div>
    );
  }

  const isDealer = profile.role === 'dealer';
  const canReview =
    isAuthenticated &&
    user?.id !== profile.id &&
    !reviews.some((r) => r.reviewer_id === user?.id);

  const initials = (profile.name || 'U')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen bg-secondary" data-testid="seller-profile-page">
      {/* Header / Cover */}
      <div className="bg-primary text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
          <div className="flex flex-col md:flex-row gap-6 md:items-end">
            <Avatar className="h-28 w-28 border-4 border-white shadow-lg" data-testid="seller-avatar">
              {profile.profile_photo ? (
                <AvatarImage src={profile.profile_photo} alt={profile.name} />
              ) : null}
              <AvatarFallback className="bg-accent text-white text-2xl font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1
                  className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight"
                  data-testid="seller-name"
                >
                  {profile.company_name || profile.name}
                </h1>
                {isDealer && (
                  <Badge className="bg-accent text-white" data-testid="verified-dealer-badge">
                    <Shield className="h-3 w-3 mr-1" />Concesionario verificado</Badge>
                )}
              </div>
              {profile.company_name && profile.company_name !== profile.name && (
                <p className="text-white/70 mt-1">Represented by {profile.name}</p>
              )}

              <div className="flex items-center gap-4 mt-4 text-white/80 flex-wrap">
                <div className="flex items-center gap-2" data-testid="seller-rating">
                  <StarRow value={profile.rating_average} size="h-4 w-4" />
                  <span className="text-sm">
                    {profile.rating_average.toFixed(1)} ({profile.rating_count} reseña{profile.rating_count === 1 ? '' : 's'})
                  </span>
                </div>

                {profile.location_city && (
                  <div className="flex items-center gap-1 text-sm">
                    <MapPin className="h-4 w-4" />
                    {profile.location_city}
                    {profile.location_state ? `, ${profile.location_state}` : ''}
                  </div>
                )}

                <div className="flex items-center gap-1 text-sm">
                  <Calendar className="h-4 w-4" />
                  Miembro desde {formatDate(profile.member_since)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar: Contact / About */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-white border border-border rounded-md p-6" data-testid="seller-contact-card">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">Contacto</h2>

              <div className="space-y-3">
                {profile.phone ? (
                  <a
                    href={`tel:${profile.phone}`}
                    className="flex items-center gap-3 p-3 rounded-sm bg-secondary hover:bg-accent hover:text-white transition-colors"
                    data-testid="seller-phone-link"
                  >
                    <Phone className="h-4 w-4" />
                    <span className="font-semibold">{profile.phone}</span>
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground">Sin teléfono</p>
                )}

                {profile.website && (
                  <a
                    href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-sm bg-secondary hover:bg-accent hover:text-white transition-colors"
                    data-testid="seller-website-link"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="truncate">{profile.website}</span>
                  </a>
                )}

                {listings.length > 0 && (
                  <Link to={`/listings/${listings[0].id}`}>
                    <Button variant="outline" className="w-full" data-testid="seller-message-btn">
                      <MessageSquare className="h-4 w-4 mr-2" />Enviar mensaje desde un anuncio</Button>
                  </Link>
                )}
              </div>
            </div>

            <div className="bg-white border border-border rounded-md p-6">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">Acerca de</h2>
              {profile.bio ? (
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap" data-testid="seller-bio">
                  {profile.bio}
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Este {isDealer ? 'concesionario' : 'vendedor'} aún no agregó una biografía.
                </p>
              )}

              <Separator className="my-5" />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  {isDealer ? <Building className="h-4 w-4" /> : <User className="h-4 w-4" />}
                  <span>{isDealer ? 'Concesionario' : profile.role === 'seller' ? 'Vendedor' : profile.role}</span>
                </div>
                <div
                  className="flex items-center gap-2 text-muted-foreground"
                  data-testid="active-listings-count"
                >
                  <Package className="h-4 w-4" />
                  {profile.active_listings_count} activo{profile.active_listings_count === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          </aside>

          {/* Main: Listings + Reviews */}
          <main className="lg:col-span-8 space-y-10">
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Inventario activo</h2>
                <span className="text-sm text-muted-foreground">
                  {profile.active_listings_count} anuncio{profile.active_listings_count === 1 ? '' : 's'}
                </span>
              </div>
              <ListingGrid
                listings={listings}
                columns={3}
                emptyMessage="Este vendedor no tiene anuncios activos en este momento."
              />
            </section>

            <section data-testid="seller-reviews-section">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Reseñas</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRow value={profile.rating_average} size="h-4 w-4" />
                    <span className="text-sm text-muted-foreground">
                      {profile.rating_average.toFixed(1)} de 5 · {profile.rating_count} reseña{profile.rating_count === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>

                {canReview && (
                  <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-accent" data-testid="write-review-btn">Escribir una reseña</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Reseña a {profile.company_name || profile.name}</DialogTitle>
                        <DialogDescription>Comparte tu experiencia para ayudar a otros compradores a decidir mejor.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium block mb-2">Tu calificación</label>
                          <StarRow value={reviewRating} onClick={setReviewRating} />
                        </div>
                        <div>
                          <label className="text-sm font-medium block mb-2">Comentarios (opcional)</label>
                          <Textarea
                            rows={4}
                            placeholder="Comparte detalles de tu experiencia..."
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            data-testid="review-comment-input"
                          />
                        </div>
                        <Button
                          className="btn-accent w-full"
                          onClick={handleSubmitReview}
                          disabled={submittingReview}
                          data-testid="submit-review-btn"
                        >
                          {submittingReview ? 'Enviando...' : 'Enviar reseña'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {reviews.length === 0 ? (
                <div className="bg-white border border-border rounded-md p-8 text-center text-muted-foreground">Aún no hay reseñas. Sé el primero en compartir tu experiencia.</div>
              ) : (
                <ul className="space-y-4">
                  {reviews.map((r) => (
                    <li
                      key={r.id}
                      className="bg-white border border-border rounded-md p-5"
                      data-testid={`review-item-${r.id}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar className="h-9 w-9">
                          {r.reviewer_photo ? <AvatarImage src={r.reviewer_photo} /> : null}
                          <AvatarFallback>
                            {(r.reviewer_name || 'U').slice(0, 1).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold leading-tight">
                            {r.reviewer_name || 'Anonymous'}
                          </p>
                          <div className="flex items-center gap-2">
                            <StarRow value={r.rating} size="h-3 w-3" />
                            <span className="text-xs text-muted-foreground">
                              {formatRelativeTime(r.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {r.comment && (
                        <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {r.comment}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
